/**
 * Dashboard BackOffice avec graphiques
 */

import { useState, useEffect, useRef } from 'react';
import { useAuth, reviewApi, TopRatedArticle, StarRating } from '@e-store/shared';
import { LayoutWithSidebar } from '../components/LayoutWithSidebar';
import { useNavigate } from 'react-router-dom';
import {
  dashboardApi,
  DashboardStats,
  ChartData,
  TopProduct as ApiTopProduct,
  VIPCustomer as ApiVIPCustomer,
  ClientWithDebt as ApiClientWithDebt,
  UnpaidInvoice,
} from '../services/dashboard.api';
import { sitesApi, Site } from '../services/sites.api';
import './DashboardPageNew.css';

interface TopProduct {
  name: string;
  sales: number;
  quantity: number;
}

interface VIPCustomer {
  name: string;
  totalSpent: number;
  orders: number;
}

interface ClientWithDebt {
  contactId: string;
  name: string;
  totalDebt: number;
  unpaidInvoices: number;
  oldestDebtDate: string;
}

interface SalesData {
  period: string;
  sales: number;
}

export function DashboardPageNew() {
  const { user } = useAuth();
  const [period, setPeriod] = useState<'7days' | 'month' | 'year'>('7days');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // État pour le filtre par site
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>(undefined);
  const [siteSearchQuery, setSiteSearchQuery] = useState('');
  const [showSiteDropdown, setShowSiteDropdown] = useState(false);
  const siteDropdownRef = useRef<HTMLDivElement>(null);

  // États pour les données réelles
  const [stats, setStats] = useState<DashboardStats>({
    totalSales: 0,
    salesGrowth: 0,
    totalOrders: 0,
    ordersGrowth: 0,
    totalCustomers: 0,
    customersGrowth: 0,
    averageOrder: 0,
    averageGrowth: 0,
  });

  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [vipCustomers, setVIPCustomers] = useState<VIPCustomer[]>([]);
  const [clientsWithDebts, setClientsWithDebts] = useState<ClientWithDebt[]>([]);
  const [salesData, setSalesData] = useState<Record<string, SalesData[]>>({
    '7days': [],
    month: [],
    year: [],
  });
  const [topRatedProducts, setTopRatedProducts] = useState<TopRatedArticle[]>([]);

  // États pour le popup factures impayées
  const [showUnpaidModal, setShowUnpaidModal] = useState(false);
  const [selectedDebtClient, setSelectedDebtClient] = useState<ClientWithDebt | null>(null);
  const [unpaidInvoices, setUnpaidInvoices] = useState<UnpaidInvoice[]>([]);
  const [loadingUnpaid, setLoadingUnpaid] = useState(false);

  const navigate = useNavigate();

  // Charger les sites au démarrage
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await sitesApi.getActiveSites();
        if (response.data?.sites) {
          setSites(response.data.sites);
          // Si l'utilisateur n'est pas admin, sélectionner son site par défaut
          if (user?.role !== 'admin' && user?.siteId) {
            setSelectedSiteId(user.siteId);
          }
        }
      } catch (err) {
        console.error('Error fetching sites:', err);
      }
    };

    fetchSites();
  }, [user]);

  // Fermer le dropdown site quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (siteDropdownRef.current && !siteDropdownRef.current.contains(event.target as Node)) {
        setShowSiteDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtrer les sites par recherche
  const filteredSites = sites.filter((site) =>
    site.name.toLowerCase().includes(siteSearchQuery.toLowerCase())
  );

  // Nom du site sélectionné
  const selectedSiteName = selectedSiteId
    ? sites.find((s) => s.id === selectedSiteId)?.name || 'Site inconnu'
    : 'Tous les sites';

  // Charger toutes les donnees du dashboard en 1 seul appel agrege
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const apiPeriod = period === '7days' ? '7d' : period;
        const response = await dashboardApi.getAll(selectedSiteId, apiPeriod);

        if (response.data) {
          const data = response.data;

          // Stats
          if (data.stats) {
            setStats(data.stats);
          }

          // Chart data
          if (data.salesChart && Array.isArray(data.salesChart)) {
            const formatted = data.salesChart.map((item: ChartData) => ({
              period: formatPeriodLabel(item.date, period),
              sales: item.sales,
            }));
            setSalesData((prev) => ({ ...prev, [period]: formatted }));
          }

          // Top products
          if (data.topProducts && Array.isArray(data.topProducts)) {
            setTopProducts(
              data.topProducts.map((p: ApiTopProduct) => ({
                name: p.articleName,
                sales: p.totalSold,
                quantity: p.quantity,
              }))
            );
          }

          // VIP clients
          if (data.topClients && Array.isArray(data.topClients)) {
            setVIPCustomers(
              data.topClients.map((c: ApiVIPCustomer) => ({
                name: c.clientName,
                totalSpent: c.totalSpent,
                orders: c.orders,
              }))
            );
          }

          // Clients with debts
          if (data.clientsWithDebts && Array.isArray(data.clientsWithDebts)) {
            setClientsWithDebts(
              data.clientsWithDebts.map((d: ApiClientWithDebt) => ({
                contactId: d.contactId,
                name: d.clientName,
                totalDebt: d.totalDebt,
                unpaidInvoices: d.unpaidInvoices,
                oldestDebtDate: d.oldestDebtDate,
              }))
            );
          }

          setError(null);
        } else if (response.error) {
          console.error('Dashboard API error:', response.error);
          setError('Erreur lors du chargement du dashboard');
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Erreur lors du chargement du dashboard');
      }
    };

    fetchDashboardData();
  }, [selectedSiteId, period]);

  // Charger les produits les mieux notes (endpoint separe)
  useEffect(() => {
    const fetchTopRated = async () => {
      try {
        const response = await reviewApi.getTopRatedArticles(5);
        if (response.data) {
          setTopRatedProducts(response.data);
        }
      } catch (err) {
        console.error('Error fetching top rated products:', err);
      }
    };

    fetchTopRated();
  }, []);

  // Fin du chargement initial
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  // Formater les labels de période
  const formatPeriodLabel = (date: string, periodType: string): string => {
    const d = new Date(date);

    if (periodType === '7days') {
      const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
      return days[d.getDay()];
    }

    if (periodType === 'month') {
      const day = d.getDate();
      return `${day}`;
    }

    if (periodType === 'year') {
      const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
      return months[d.getMonth()];
    }

    return date;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-MG', {
      style: 'currency',
      currency: 'MGA',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const getMaxSales = () => {
    const data = salesData[period];
    return Math.max(...data.map(d => d.sales));
  };

  const getBarHeight = (value: number) => {
    const max = getMaxSales();
    return (value / max) * 100;
  };

  // Handler pour cliquer sur un client avec dettes
  const handleDebtClientClick = async (client: ClientWithDebt) => {
    setSelectedDebtClient(client);
    setShowUnpaidModal(true);
    setLoadingUnpaid(true);

    try {
      const response = await dashboardApi.getUnpaidInvoicesByClient(client.contactId, selectedSiteId);
      if (response.data && Array.isArray(response.data)) {
        setUnpaidInvoices(response.data);
      }
    } catch (err) {
      console.error('Error fetching unpaid invoices:', err);
      setUnpaidInvoices([]);
    } finally {
      setLoadingUnpaid(false);
    }
  };

  // Naviguer vers la facture dans la liste des factures
  const handleNavigateToInvoice = (invoiceId: string) => {
    setShowUnpaidModal(false);
    navigate(`/invoices?id=${invoiceId}`);
  };

  if (isLoading) {
    return (
      <LayoutWithSidebar>
        <div className="dashboard-loading">
          <div className="spinner" />
          <p>Chargement du tableau de bord...</p>
        </div>
      </LayoutWithSidebar>
    );
  }

  return (
    <LayoutWithSidebar>
      <div className="dashboard-new">
        <div className="dashboard-header-new">
          <div>
            <h1>Tableau de bord</h1>
            <p>Bienvenue, {user?.firstName} {user?.lastName}</p>
          </div>

          {/* Filtre par site (Select2 style) */}
          <div className="site-filter-wrapper" ref={siteDropdownRef}>
            <div
              className="site-filter-input"
              onClick={() => setShowSiteDropdown(!showSiteDropdown)}
            >
              <span className="site-filter-icon">🏪</span>
              <span className="site-filter-value">{selectedSiteName}</span>
              <span className="site-filter-arrow">{showSiteDropdown ? '▲' : '▼'}</span>
            </div>

            {showSiteDropdown && (
              <div className="site-filter-dropdown">
                <div className="site-filter-search">
                  <input
                    type="text"
                    placeholder="Rechercher un site..."
                    value={siteSearchQuery}
                    onChange={(e) => setSiteSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                </div>
                <div className="site-filter-options">
                  {/* Option "Tous les sites" - uniquement pour admin */}
                  {user?.role === 'admin' && (
                    <div
                      className={`site-filter-option ${!selectedSiteId ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedSiteId(undefined);
                        setShowSiteDropdown(false);
                        setSiteSearchQuery('');
                      }}
                    >
                      <span className="site-option-icon">🌐</span>
                      <span className="site-option-name">Tous les sites</span>
                    </div>
                  )}
                  {filteredSites.map((site) => (
                    <div
                      key={site.id}
                      className={`site-filter-option ${selectedSiteId === site.id ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedSiteId(site.id);
                        setShowSiteDropdown(false);
                        setSiteSearchQuery('');
                      }}
                    >
                      <span className="site-option-icon">{site.type === 'siege' ? '🏢' : '🏪'}</span>
                      <span className="site-option-name">{site.name}</span>
                      <span className="site-option-type">{site.type === 'siege' ? 'Siège' : 'Magasin'}</span>
                    </div>
                  ))}
                  {filteredSites.length === 0 && (
                    <div className="site-filter-empty">Aucun site trouvé</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="error-message" style={{ padding: '16px', marginBottom: '24px', background: '#fee', border: '1px solid #fcc', borderRadius: '8px', color: '#c00' }}>
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-header">
              <span className="stat-icon">💰</span>
              <span className={`stat-badge ${stats.salesGrowth >= 0 ? 'positive' : 'negative'}`}>
                {stats.salesGrowth >= 0 ? '↗' : '↘'} {Math.abs(stats.salesGrowth)}%
              </span>
            </div>
            <div className="stat-value">{formatCurrency(stats.totalSales)}</div>
            <div className="stat-label">Ventes totales</div>
          </div>

          <div className="stat-card">
            <div className="stat-header">
              <span className="stat-icon">📦</span>
              <span className={`stat-badge ${stats.ordersGrowth >= 0 ? 'positive' : 'negative'}`}>
                {stats.ordersGrowth >= 0 ? '↗' : '↘'} {Math.abs(stats.ordersGrowth)}%
              </span>
            </div>
            <div className="stat-value">{stats.totalOrders}</div>
            <div className="stat-label">Commandes</div>
          </div>

          <div className="stat-card">
            <div className="stat-header">
              <span className="stat-icon">👥</span>
              <span className={`stat-badge ${stats.customersGrowth >= 0 ? 'positive' : 'negative'}`}>
                {stats.customersGrowth >= 0 ? '↗' : '↘'} {Math.abs(stats.customersGrowth)}%
              </span>
            </div>
            <div className="stat-value">{stats.totalCustomers}</div>
            <div className="stat-label">Clients actifs</div>
          </div>

          <div className="stat-card">
            <div className="stat-header">
              <span className="stat-icon">🎯</span>
              <span className={`stat-badge ${stats.averageGrowth >= 0 ? 'positive' : 'negative'}`}>
                {stats.averageGrowth >= 0 ? '↗' : '↘'} {Math.abs(stats.averageGrowth)}%
              </span>
            </div>
            <div className="stat-value">{formatCurrency(stats.averageOrder)}</div>
            <div className="stat-label">Panier moyen</div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="charts-grid">
          {/* Sales Chart */}
          <div className="chart-card">
            <div className="chart-header">
              <h2>Ventes réalisées</h2>
              <div className="period-selector">
                <button
                  className={period === '7days' ? 'active' : ''}
                  onClick={() => setPeriod('7days')}
                >
                  7 jours
                </button>
                <button
                  className={period === 'month' ? 'active' : ''}
                  onClick={() => setPeriod('month')}
                >
                  Mois
                </button>
                <button
                  className={period === 'year' ? 'active' : ''}
                  onClick={() => setPeriod('year')}
                >
                  Année
                </button>
              </div>
            </div>
            <div className="bar-chart">
              {salesData[period].map((data, index) => (
                <div key={index} className="bar-wrapper">
                  <div className="bar-container">
                    <div
                      className="bar"
                      style={{ height: `${getBarHeight(data.sales)}%` }}
                    >
                      <span className="bar-tooltip">{formatCurrency(data.sales)}</span>
                    </div>
                  </div>
                  <span className="bar-label">{data.period}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Top Products */}
          <div className="chart-card">
            <div className="chart-header">
              <h2>Articles les mieux vendus</h2>
            </div>
            <div className="top-list">
              {topProducts.map((product, index) => (
                <div key={index} className="top-item">
                  <div className="top-rank">{index + 1}</div>
                  <div className="top-info">
                    <div className="top-name">{product.name}</div>
                    <div className="top-meta">
                      {product.quantity} unités • {formatCurrency(product.sales)}
                    </div>
                  </div>
                  <div className="top-progress">
                    <div
                      className="top-progress-bar"
                      style={{
                        width: `${(product.sales / topProducts[0].sales) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* VIP Customers */}
          <div className="chart-card">
            <div className="chart-header">
              <h2>Clients VIP</h2>
              <span className="chart-subtitle">Top acheteurs</span>
            </div>
            <div className="vip-list">
              {vipCustomers.map((customer, index) => (
                <div key={index} className="vip-item">
                  <div className="vip-avatar">
                    {customer.name.charAt(0)}
                  </div>
                  <div className="vip-info">
                    <div className="vip-name">{customer.name}</div>
                    <div className="vip-meta">
                      {customer.orders} commandes
                    </div>
                  </div>
                  <div className="vip-amount">
                    {formatCurrency(customer.totalSpent)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Clients avec dettes */}
          <div className="chart-card">
            <div className="chart-header">
              <h2>Clients avec dettes</h2>
              <span className="chart-subtitle">Factures impayées</span>
            </div>
            <div className="debt-list">
              {clientsWithDebts.length === 0 ? (
                <div className="empty-state">
                  <p>Aucune dette en cours</p>
                </div>
              ) : (
                clientsWithDebts.map((client, index) => (
                  <div
                    key={index}
                    className="debt-item debt-item-clickable"
                    onClick={() => handleDebtClientClick(client)}
                    title="Cliquer pour voir les factures impayées"
                  >
                    <div className="debt-avatar">
                      {client.name.charAt(0)}
                    </div>
                    <div className="debt-info">
                      <div className="debt-name">{client.name}</div>
                      <div className="debt-meta">
                        {client.unpaidInvoices} facture{client.unpaidInvoices > 1 ? 's' : ''} impayée{client.unpaidInvoices > 1 ? 's' : ''}
                        {' • '}
                        Depuis {new Date(client.oldestDebtDate).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                    <div className="debt-amount">
                      {formatCurrency(client.totalDebt)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Top 5 produits les mieux notés */}
          <div className="chart-card">
            <div className="chart-header">
              <h2>⭐ Produits les mieux notés</h2>
              <span className="chart-subtitle">Top 5 par étoiles</span>
            </div>
            <div className="top-rated-list">
              {topRatedProducts.length === 0 ? (
                <div className="empty-state">
                  <p>Aucun avis pour le moment</p>
                </div>
              ) : (
                topRatedProducts.map((product) => (
                  <div key={product.articleId} className="top-rated-item">
                    <div className="top-rated-rank">#{product.rank}</div>
                    {product.articleImage ? (
                      <img
                        src={product.articleImage}
                        alt={product.articleName}
                        className="top-rated-image"
                      />
                    ) : (
                      <div className="top-rated-no-image">📦</div>
                    )}
                    <div className="top-rated-info">
                      <div className="top-rated-name">{product.articleName}</div>
                      <div className="top-rated-ref">{product.articleReference}</div>
                      <div className="top-rated-rating">
                        <StarRating
                          rating={product.averageRating}
                          totalReviews={product.totalReviews}
                          size="small"
                          showCount={true}
                        />
                      </div>
                    </div>
                    <div className="top-rated-score">
                      {product.averageRating.toFixed(1)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal factures impayées */}
      {showUnpaidModal && selectedDebtClient && (
        <div className="modal-overlay" onClick={() => setShowUnpaidModal(false)}>
          <div className="modal-content unpaid-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Factures impayées - {selectedDebtClient.name}</h2>
              <button className="close-btn" onClick={() => setShowUnpaidModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="unpaid-summary">
                <div className="unpaid-stat">
                  <span className="stat-label">Total dû</span>
                  <span className="stat-value debt-color">{formatCurrency(selectedDebtClient.totalDebt)}</span>
                </div>
                <div className="unpaid-stat">
                  <span className="stat-label">Factures</span>
                  <span className="stat-value">{selectedDebtClient.unpaidInvoices}</span>
                </div>
              </div>

              {loadingUnpaid ? (
                <div className="loading-spinner">Chargement...</div>
              ) : unpaidInvoices.length === 0 ? (
                <div className="empty-state">Aucune facture impayée trouvée</div>
              ) : (
                <div className="unpaid-list">
                  <table className="unpaid-table">
                    <thead>
                      <tr>
                        <th>N° Facture</th>
                        <th>Date</th>
                        <th>Total TTC</th>
                        <th>Payé</th>
                        <th>Reste dû</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {unpaidInvoices.map((invoice) => (
                        <tr key={invoice.id}>
                          <td>{invoice.invoiceNumber}</td>
                          <td>{new Date(invoice.createdAt).toLocaleDateString('fr-FR')}</td>
                          <td>{formatCurrency(invoice.totalTTC)}</td>
                          <td>{formatCurrency(invoice.totalPaid)}</td>
                          <td className="debt-color">{formatCurrency(invoice.remainingDebt)}</td>
                          <td>
                            <button
                              className="btn-view"
                              onClick={() => handleNavigateToInvoice(invoice.id)}
                              title="Voir la facture"
                            >
                              👁️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </LayoutWithSidebar>
  );
}
